import React from "react";
import { motion } from "framer-motion";
import { X, Check, Moon, Zap } from "lucide-react";
import { RevealOnScroll } from "../../marketingAgency/shared";
import {
  SPEED_GAP_AGENCY_PAIN,
  SPEED_GAP_AI_PATH,
  SPEED_GAP_HUMAN_PATH,
  SPEED_GAP_OUTCOME_BADGE,
  SPEED_GAP_SPEED_HERO,
  SPEED_GAP_THESIS,
} from "./speedGapContent";

const AgencyPainCard = () => (
  <div
    className="rounded-xl border border-red-500/25 bg-red-500/5 px-5 py-5 mb-8"
    data-testid="speed-gap-agency-pain"
  >
    <p className="text-[10px] font-mono text-red-400/90 tracking-widest uppercase mb-2">
      {SPEED_GAP_AGENCY_PAIN.label}
    </p>
    <p className="text-sm sm:text-base text-partner-muted font-body leading-relaxed">{SPEED_GAP_AGENCY_PAIN.body}</p>
  </div>
);

const OutcomeBadge = () => (
  <div
    className="inline-flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-xl border border-[#00D2FF]/35 bg-[#00D2FF]/10 px-4 py-3 mt-6"
    data-testid="speed-gap-outcome-badge"
  >
    <span className="font-mono text-xs sm:text-sm font-bold text-partner-accent tracking-wide">
      {SPEED_GAP_OUTCOME_BADGE.metric}
    </span>
    <span className="hidden sm:block text-partner-accent/40 font-mono text-xs">|</span>
    <span className="font-mono text-xs sm:text-sm text-partner-muted">{SPEED_GAP_OUTCOME_BADGE.context}</span>
  </div>
);

const WorkflowStep = ({ step, variant }) => (
  <div className="flex gap-3 items-start">
    <span
      className={`font-mono text-[10px] sm:text-xs shrink-0 pt-0.5 tabular-nums ${
        variant === "ai" ? "text-partner-accent" : "text-red-400/80"
      }`}
    >
      {step.time}
    </span>
    <span className={`text-sm font-body leading-relaxed ${variant === "ai" ? "text-partner" : "text-partner-muted"}`}>
      {step.text}
    </span>
  </div>
);

const WorkflowPath = ({ path, variant, delay = 0 }) => {
  const isAi = variant === "ai";
  const Icon = isAi ? Zap : Moon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay }}
      className={`rounded-xl border p-5 sm:p-6 ${
        isAi ? "border-[#00D2FF]/30 bg-[#00D2FF]/5" : "border-red-500/25 bg-red-500/5"
      }`}
      data-testid={`speed-gap-path-${variant}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <div
          className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${
            isAi ? "border-[#00D2FF]/35 bg-[#00D2FF]/10" : "border-red-500/30 bg-red-500/10"
          }`}
        >
          <Icon className={`w-4 h-4 ${isAi ? "text-partner-accent" : "text-red-400"}`} />
        </div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-partner-muted">{path.label}</p>
      </div>

      <div className="space-y-3 mb-4">
        {path.steps.map((step) => (
          <WorkflowStep key={`${step.time}-${step.text}`} step={step} variant={variant} />
        ))}
      </div>

      <div
        className={`flex items-center gap-2 pt-3 border-t ${
          isAi ? "border-[#00D2FF]/20" : "border-red-500/20"
        }`}
      >
        {isAi ? (
          <Check className="w-4 h-4 text-partner-accent shrink-0" />
        ) : (
          <X className="w-4 h-4 text-red-400 shrink-0" />
        )}
        <span className={`text-sm font-bold ${isAi ? "text-partner-accent" : "text-red-400"}`}>{path.result}</span>
      </div>
    </motion.div>
  );
};

const SpeedHero = () => (
  <div className="rounded-xl border border-partner bg-[#11151F] px-5 sm:px-8 py-8 sm:py-10" data-testid="speed-gap-hero-metric">
    <div className="text-center mb-6">
      <motion.p
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="font-mono text-5xl sm:text-6xl font-bold text-partner-accent tabular-nums mb-1"
      >
        {SPEED_GAP_SPEED_HERO.value}
      </motion.p>
      <p className="text-xs font-mono text-partner-muted uppercase tracking-wider">vs. 45 min office workflow</p>
    </div>

    <p className="text-sm sm:text-base font-semibold text-partner text-center mb-4">
      {SPEED_GAP_SPEED_HERO.contrastHeadline}
    </p>

    <ul className="flex flex-wrap justify-center gap-2 mb-6">
      {SPEED_GAP_SPEED_HERO.pipeline.map((step) => (
        <li
          key={step}
          className="rounded-full border border-[#00D2FF]/25 bg-[#00D2FF]/5 px-3 py-1.5 text-xs font-body text-partner-muted"
        >
          {step}
        </li>
      ))}
    </ul>

    <p className="text-sm sm:text-base text-partner font-body text-center leading-relaxed max-w-2xl mx-auto">
      {SPEED_GAP_SPEED_HERO.caption}
    </p>

    <div className="flex justify-center">
      <OutcomeBadge />
    </div>
  </div>
);

const SpeedGapSection = ({ id = "speed-gap" }) => (
  <section
    id={id}
    className="section-padding relative overflow-hidden border-y border-partner"
    data-testid="speed-gap-log"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
          {SPEED_GAP_THESIS.headline}
        </h2>
        <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-6">{SPEED_GAP_THESIS.subline}</p>
        <AgencyPainCard />
      </RevealOnScroll>

      <RevealOnScroll delay={0.06}>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-8" data-testid="speed-gap-split-visual">
          <WorkflowPath path={SPEED_GAP_HUMAN_PATH} variant="human" delay={0} />
          <WorkflowPath path={SPEED_GAP_AI_PATH} variant="ai" delay={0.08} />
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.1}>
        <SpeedHero />
      </RevealOnScroll>
    </div>
  </section>
);

export default SpeedGapSection;
